import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CloudOff, Loader2, Receipt } from "lucide-react";
import AdminDesk from "../components/AdminDesk";
import AdminFloorGrid from "../components/AdminFloorGrid";
import AdminTransactionChrome from "../transactions/AdminTransactionChrome";
import AdminTransactionFiltersRail from "../transactions/AdminTransactionFiltersRail";
import TransactionFloorIndex from "../transactions/floor/TransactionFloorIndex";
import TransactionFloorDetail from "../transactions/floor/TransactionFloorDetail";
import { fetchAdminTransactionDesk } from "../transactions/api/fetchAdminTransaction";
import { clientFilterTransactions, sortTransactions } from "../transactions/constants";
import "../styles/admin-markets.css";
import "../styles/admin-markets-floor.css";
import "../transactions/admin-transaction.css";

const PAGE_SIZE = 15;

export default function TransactionPage() {
  const [desk, setDesk] = useState("all");
  const [status, setStatus] = useState("All");
  const [currency, setCurrency] = useState("All");
  const [sortKey, setSortKey] = useState("newest");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 320);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async ({ pageNum = 1, refresh = false } = {}) => {
      setLoading(true);
      if (refresh) setRefreshing(true);
      setError(null);

      try {
        const data = await fetchAdminTransactionDesk({
          page: pageNum,
          limit: PAGE_SIZE,
          status: status === "All" ? "all" : status,
          desk,
          currency: currency === "All" ? "all" : currency,
          search: debouncedSearch,
          refresh,
        });

        setTransactions(data?.transactions || []);
        setSummary(data?.summary || null);
        setPagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
          limit: data?.pagination?.limit || PAGE_SIZE,
        }));
      } catch (err) {
        setTransactions([]);
        setError(err.response?.data?.error || err.message || "Failed to load transaction desk");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [desk, status, currency, debouncedSearch]
  );

  const fetchPage = useCallback(
    (pageNum, refresh = false) => {
      setPage(pageNum);
      load({ pageNum, refresh });
    },
    [load]
  );

  useEffect(() => {
    fetchPage(1);
  }, [desk, status, currency, debouncedSearch, fetchPage]);

  const handleRefresh = () => load({ pageNum: page, refresh: true });

  const handlePageChange = (nextPage) => {
    if (nextPage === page || nextPage < 1) return;
    fetchPage(nextPage);
  };

  const displayTransactions = useMemo(() => {
    let list = clientFilterTransactions(transactions, search);
    return sortTransactions(list, sortKey);
  }, [transactions, search, sortKey]);

  const selectedTx = useMemo(() => {
    if (!selectedId) return null;
    return (
      displayTransactions.find((tx) => tx._id === selectedId) ||
      transactions.find((tx) => tx._id === selectedId) ||
      null
    );
  }, [displayTransactions, transactions, selectedId]);

  useEffect(() => {
    if (!displayTransactions.length) {
      setSelectedId(null);
      return;
    }
    const stillVisible = selectedId && displayTransactions.some((tx) => tx._id === selectedId);
    if (!stillVisible) {
      setSelectedId(displayTransactions[0]._id);
    }
  }, [displayTransactions, selectedId]);

  const activeFilterCount = [
    desk !== "all",
    status !== "All",
    currency !== "All",
    sortKey !== "newest",
  ].filter(Boolean).length;

  const handleClearFilters = () => {
    setDesk("all");
    setStatus("All");
    setCurrency("All");
    setSortKey("newest");
    setSearch("");
  };

  const railProps = {
    desk,
    status,
    currency,
    sortKey,
    summary,
    onDeskChange: setDesk,
    onStatusChange: setStatus,
    onCurrencyChange: setCurrency,
    onSortChange: setSortKey,
    onClear: handleClearFilters,
    activeCount: activeFilterCount,
  };

  const isLoading = loading && !refreshing && transactions.length === 0;

  return (
    <div className="am-page atx-page">
      <div className="am-aurora" aria-hidden>
        <span className="am-aurora-a atx-aurora" />
      </div>

      <AdminTransactionChrome
        search={search}
        onSearchChange={setSearch}
        loading={loading || refreshing}
        onRefresh={handleRefresh}
        filtersOpen={filtersOpen}
        onFiltersToggle={() => setFiltersOpen((v) => !v)}
        activeFilterCount={activeFilterCount}
        summary={summary}
        totalLoaded={pagination.total || transactions.length}
      />

      <AdminDesk
        sectionKey="transactions"
        sidebarLabel="Transaction filters"
        sidebar={<AdminTransactionFiltersRail {...railProps} />}
      >
            {error ? (
              <div className="atx-error-banner" role="alert">
                <span>{error}</span>
                <button type="button" onClick={() => setError(null)}>
                  Dismiss
                </button>
              </div>
            ) : null}

            <div className="am-board am-board-floor">
              {isLoading ? (
                <div className="am-state">
                  <Loader2 size={28} className="am-spin" />
                  <p>Loading transactions…</p>
                </div>
              ) : error && !transactions.length ? (
                <div className="am-state">
                  <CloudOff size={28} />
                  <p>{error}</p>
                  <button type="button" className="am-chrome-btn am-chrome-btn-accent" onClick={handleRefresh}>
                    Try again
                  </button>
                </div>
              ) : displayTransactions.length === 0 && !loading ? (
                <div className="am-state">
                  <Receipt size={28} />
                  <h2>No transactions match</h2>
                  <p>
                    {desk === "needs_action"
                      ? "Nothing in the action queue — all caught up."
                      : "Try clearing filters or adjusting your search."}
                  </p>
                  {activeFilterCount || search ? (
                    <button type="button" className="am-chrome-btn am-chrome-btn-accent" onClick={handleClearFilters}>
                      Clear filters
                    </button>
                  ) : null}
                </div>
              ) : (
                <AdminFloorGrid
                  sectionKey="transactions"
                  index={
                    <TransactionFloorIndex
                      transactions={displayTransactions}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      desk={desk}
                      pagination={pagination}
                      loading={loading}
                      onPageChange={handlePageChange}
                    />
                  }
                  detail={<TransactionFloorDetail tx={selectedTx} />}
                />
              )}
            </div>
      </AdminDesk>

      {filtersOpen ? (
        <div className="am-filters-backdrop" onClick={() => setFiltersOpen(false)} role="presentation">
          <div className="am-filters-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Transaction filters">
            <AdminTransactionFiltersRail {...railProps} compact onClose={() => setFiltersOpen(false)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
